<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Teknik-kort
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Tre kortvarianter från
      <span
        class="font-mono text-[13px] bg-[#f2f4f6] px-1.5 py-0.5 rounded-[5px]"
        >Tool/Index.vue</span
      >: hero-bildkort, verktygsrad och rutnätskort.
    </p>

    <!-- Hero / featured card -->
    <PreviewBox label="Hjältekort — stor bildvariant">
      <div
        class="relative overflow-hidden rounded-3xl w-full max-w-[420px]"
        style="height: 320px"
      >
        <div
          class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
        />
        <div
          class="absolute inset-0 flex flex-col justify-end p-6 md:p-8"
          style="
            background: linear-gradient(
              to top,
              rgba(0, 0, 0, 0.85) 0%,
              rgba(0, 0, 0, 0.2) 55%,
              rgba(0, 0, 0, 0) 100%
            );
          "
        >
          <span
            class="inline-block px-3 py-1 rounded-full mb-3 text-[10px] font-bold tracking-widest uppercase w-fit bg-teal-500 text-white"
            >SURDEG</span
          >
          <h2 class="font-[Poppins] font-bold text-white m-0 mb-2 text-[22px]">
            Så skapar du en aktiv surdeg
          </h2>
          <p class="text-white/85 text-sm leading-5 mb-5 max-w-xs m-0">
            Lär dig bygga upp en stabil surdeg från scratch – dag för dag.
          </p>
          <span
            class="inline-flex items-center gap-2 text-teal-400 font-bold text-sm"
          >
            Visa steg för steg <ArrowRight :size="14" />
          </span>
        </div>
      </div>
    </PreviewBox>

    <!-- Tool card -->
    <PreviewBox label="Verktygsrad — ikon + badge + titel + pil">
      <div
        class="flex flex-col gap-3 p-5 rounded-3xl border border-[#eceef0] bg-white w-full max-w-[480px] cursor-pointer hover:opacity-90 transition-opacity"
      >
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center rounded-2xl shrink-0 bg-teal-500/10 w-14 h-14"
          >
            <span class="text-[28px]">⚖️</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-block px-2.5 py-0.5 rounded-full bg-teal-500 text-white text-[10px] font-bold tracking-[0.8px]"
                >VERKTYG</span
              >
            </div>
            <h3
              class="font-[Poppins] font-bold text-[17px] text-[#191c1e] m-0 leading-tight"
            >
              Degkalkylator
            </h3>
          </div>
          <ArrowRight :size="18" class="text-teal-500 shrink-0" />
        </div>
        <p class="text-sm text-[#6c7a77] leading-5 m-0">
          Beräkna rätt mängder för jäst-deg, surdeg och berikad deg med
          bagarprocent. Stöd för flera satser och jästtyper.
        </p>
      </div>
    </PreviewBox>

    <!-- Grid card -->
    <PreviewBox label="Rutnätskort — bild + badge + titel + ingress">
      <div
        class="rounded-2xl overflow-hidden border border-[#eceef0] bg-white w-full max-w-[280px] cursor-pointer hover:shadow-md transition-shadow"
      >
        <div class="overflow-hidden" style="height: 180px">
          <div
            class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
          />
        </div>
        <div class="p-4">
          <span
            class="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 bg-teal-500 text-white"
            >KNÅDNING</span
          >
          <h3 class="font-[Poppins] font-bold text-[15px] text-[#191c1e] mb-1">
            Fensterfenster-testet
          </h3>
          <p class="text-xs text-[#6c7a77] leading-5 line-clamp-2">
            Hur du vet när degen är tillräckligt knådad med ett enkelt
            transparenstest.
          </p>
          <p class="text-xs text-[#9ca3af] mt-2">5 min läsning</p>
        </div>
      </div>
    </PreviewBox>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import { ArrowRight } from "lucide-vue-next";
import PreviewBox from "../components/PreviewBox.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlHero = `<!-- Hjältekort -->
<Link :href="technique.path"
  class="relative overflow-hidden rounded-3xl block no-underline"
  style="height: clamp(260px, 40vw, 440px)">
  <img :src="technique.heroImageUrl" class="w-full h-full object-cover" />
  <div v-else class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600" />
  <div class="absolute inset-0 flex flex-col justify-end p-6 md:p-8"
    style="background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)">
    <span class="inline-block px-3 py-1 rounded-full mb-3 text-[10px] font-bold tracking-widest uppercase w-fit text-white"
      :style="{ backgroundColor: technique.badgeColor }">{{ technique.badge }}</span>
    <h2 class="font-[Poppins] font-bold text-white m-0 mb-2"
      style="font-size: clamp(22px, 3vw, 28px)">{{ technique.title }}</h2>
    <p class="text-white/85 text-sm leading-5 mb-5 max-w-xs m-0 line-clamp-3">{{ technique.intro }}</p>
    <span class="inline-flex items-center gap-2 text-teal-400 font-bold text-sm">
      Visa steg för steg <ArrowRight :size="14" />
    </span>
  </div>
</Link>`;

const htmlTool = `<!-- Verktygsrad -->
<Link :href="doughCalculatorPath"
  class="flex flex-col gap-3 p-4 sm:p-5 md:p-6 rounded-2xl md:rounded-3xl
         hover:opacity-90 transition-opacity border border-[#eceef0] bg-white no-underline">
  <div class="flex items-center gap-3">
    <div class="flex items-center justify-center rounded-2xl shrink-0
                bg-teal-500/10 w-12 h-12 sm:w-14 sm:h-14">
      <span class="text-[24px] sm:text-[28px]">⚖️</span>
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1">
        <span class="inline-block px-2.5 py-0.5 rounded-full bg-teal-500
                     text-white text-[10px] font-bold tracking-[0.8px]">VERKTYG</span>
      </div>
      <h3 class="font-[Poppins] font-bold text-[16px] sm:text-[17px] text-[#191c1e] m-0 leading-tight">
        Degkalkylator
      </h3>
    </div>
    <ArrowRight :size="18" class="text-teal-500 shrink-0" />
  </div>
  <p class="text-sm text-[#6c7a77] leading-5 m-0">Beskrivning…</p>
</Link>`;

const htmlGrid = `<!-- Rutnätskort -->
<Link :href="technique.path"
  class="group rounded-2xl overflow-hidden border border-[#eceef0]
         bg-white hover:shadow-md transition-shadow no-underline">
  <div class="overflow-hidden" style="height: 180px">
    <img :src="technique.heroImageUrl" class="w-full h-full object-cover
         group-hover:scale-105 transition-transform duration-300" />
    <div v-else class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600" />
  </div>
  <div class="p-4">
    <span class="inline-block px-2.5 py-0.5 rounded-full text-[10px]
                 font-bold tracking-widest uppercase mb-2 text-white"
      :style="{ backgroundColor: technique.badgeColor }">{{ technique.badge }}</span>
    <h3 class="font-[Poppins] font-bold text-[15px] text-[#191c1e] mb-1">{{ technique.title }}</h3>
    <p class="text-xs text-[#6c7a77] leading-5 line-clamp-2">{{ technique.intro }}</p>
    <p class="text-xs text-[#9ca3af] mt-2">{{ technique.readTime }}</p>
  </div>
</Link>`;

const snippetTabs = [
  { label: "Hjältekort", code: htmlHero, lang: "html" },
  { label: "Verktygsrad", code: htmlTool, lang: "html" },
  { label: "Rutnätskort", code: htmlGrid, lang: "html" },
];
</script>
